// javascript
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventIcon from '@mui/icons-material/Event';
import TimerIcon from '@mui/icons-material/Timer';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import UpdateIcon from '@mui/icons-material/Update';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import cambioEstadoService from '../services/cambioEstadoService';
import EventoService from '../services/eventoService';
import NotificacionService from '../services/notificacionService';
import { subirArchivo, obtenerArchivosPorTramite, descargarArchivo, eliminarArchivo } from '../services/archivoService';

// Función auxiliar para obtener la fecha actual en formato YYYY-MM-DD
const obtenerFechaActual = () => {
    const hoy = new Date();
    return hoy.toISOString().split('T')[0];
};

function GestorEstadoEsperaDictamen({ tramite, onEstadoCambiado }) {
    const [cargando, setCargando] = useState(false);
    const [juntaInfo, setJuntaInfo] = useState(null);
    const [cargandoJunta, setCargandoJunta] = useState(true);
    const [mostrarFormularioDictamen, setMostrarFormularioDictamen] = useState(false);
    const [mostrarModalNotificacion, setMostrarModalNotificacion] = useState(false);
    const [canalesSeleccionados, setCanalesSeleccionados] = useState({ email: false, inapp: false });
    const [enviandoNotificacion, setEnviandoNotificacion] = useState(false);

    // --- Estados para el formulario y carga de archivos ---
    const [datosDictamen, setDatosDictamen] = useState({ resultado: '', fecha: obtenerFechaActual(), porcentaje: '' });
    const [archivos, setArchivos] = useState([]);
    const [cargandoArchivos, setCargandoArchivos] = useState(false);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [mostrarOpcionApelacion, setMostrarOpcionApelacion] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const cargarJuntaMedica = async () => {
            setCargandoJunta(true);
            try {
                const response = await EventoService.getEventosPorTramite(tramite.idTramite);
                const ultimoEventoJunta = response.data.filter(e => e.tipoEvento === 'Junta Médica').pop();
                setJuntaInfo(ultimoEventoJunta);
            } catch (error) {
                console.error("Error al cargar detalles de la junta:", error);
            } finally {
                setCargandoJunta(false);
            }
        };
        cargarJuntaMedica();
    }, [tramite.idTramite]);

    const formatearFecha = (fechaString) => {
        if (!fechaString) return 'No disponible';
        const fecha = new Date(fechaString);
        const fechaLocal = new Date(fecha.valueOf() + fecha.getTimezoneOffset() * 60000);
        return fechaLocal.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    const calcularPlazo = (fechaJunta) => {
        if (!fechaJunta) return 'No disponible';
        const fecha = new Date(fechaJunta);
        fecha.setDate(fecha.getDate() + 30); // Plazo estimado de 30 días
        return formatearFecha(fecha.toISOString());
    };

    // --- LÓGICA DE GESTIÓN DE ARCHIVOS (reutilizada) ---
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleSeleccionarArchivos = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;
        const nuevosArchivos = files.map(file => ({
            id: `local_${Date.now()}_${Math.random()}`,
            file, nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            estado: "pendiente", esDelBackend: false
        }));
        setArchivos(prev => [...prev, ...nuevosArchivos]);
        if (event.target) event.target.value = "";
    };

    const handleSubirArchivos = async () => {
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');
        if (archivosPendientes.length === 0) return [];

        setSubiendoArchivos(true);
        const archivosSubidos = [];
        for (const archivo of archivosPendientes) {
            try {
                const archivoSubido = await subirArchivo(tramite.idTramite, archivo.file);
                archivosSubidos.push(archivoSubido);
                setArchivos(prev => prev.map(item =>
                    item.id === archivo.id ? { ...item, estado: "subido", idArchivo: archivoSubido.idArchivo, esDelBackend: true } : item
                ));
            } catch (error) {
                setArchivos(prev => prev.map(item =>
                    item.id === archivo.id ? { ...item, estado: "error" } : item
                ));
                toast.error(`Error subiendo ${archivo.nombre}`);
            }
        }
        setSubiendoArchivos(false);
        return archivosSubidos;
    };

    const handleEliminarArchivo = (id) => {
        const archivo = archivos.find(a => a.id === id);
        if (archivo && window.confirm(`¿Seguro que quieres eliminar "${archivo.nombre}"?`)) {
            if (archivo.esDelBackend) {
                toast.warn("La eliminación de archivos subidos no está implementada en este componente.");
            } else {
                setArchivos(prev => prev.filter(a => a.id !== id));
                toast.info(`Archivo "${archivo.nombre}" quitado de la lista.`);
            }
        }
    };

    const calcularDiasHabiles = (fechaInicio, dias) => {
        // Crear una copia de la fecha para no modificar la original
        let fecha = new Date(fechaInicio);
        fecha.setHours(0, 0, 0, 0); // Asegurar que sea medianoche
        
        let diasAgregados = 0;
        while (diasAgregados < dias) {
            fecha.setDate(fecha.getDate() + 1);
            // Si no es sábado (6) ni domingo (0), cuenta como día hábil
            if (fecha.getDay() !== 0 && fecha.getDay() !== 6) {
                diasAgregados++;
            }
        }
        return fecha;
    };

    const handleConfirmarDictamen = async () => {
        if (!datosDictamen.resultado || !datosDictamen.fecha) {
            toast.error("Debe indicar el resultado y la fecha del dictamen.");
            return;
        }
        if (!datosDictamen.porcentaje || datosDictamen.porcentaje === '') {
            toast.error("Debe indicar el porcentaje de incapacidad del dictamen.");
            return;
        }

        const porcentaje = parseFloat(datosDictamen.porcentaje);
        
        // Validación: Rechazado no puede tener porcentaje >= 66
        if (datosDictamen.resultado === "Rechazado" && porcentaje >= 66) {
            toast.error("❌ Error: Un dictamen Rechazado no puede tener un porcentaje mayor o igual a 66%.");
            return;
        }
        
        // Validación: Aprobado no puede tener porcentaje < 66
        if (datosDictamen.resultado === "Aprobado" && porcentaje < 66) {
            toast.error("❌ Error: Un dictamen Aprobado debe tener un porcentaje mayor o igual a 66%.");
            return;
        }
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');
        if (archivosPendientes.length === 0) {
            toast.error("Debe adjuntar el archivo del dictamen.");
            return;
        }

        setCargando(true);
        try {
            const archivosSubidos = await handleSubirArchivos();
            if (archivosSubidos.length !== archivosPendientes.length) {
                toast.error("No se pudieron subir todos los archivos. Por favor, intente de nuevo.");
                throw new Error("Fallo en la subida de archivos");
            }

            const porcentaje = parseFloat(datosDictamen.porcentaje);
            const fechaNotificacion = new Date(datosDictamen.fecha + 'T00:00:00'); // Asegurar que sea medianoche
            const fechaLimiteApelacion = calcularDiasHabiles(fechaNotificacion, 5);
            
            // Formatear fecha límite para el comentario
            const fechaLimiteFormateada = formatearFecha(fechaLimiteApelacion.toISOString().split('T')[0]);
            const fechaNotificacionFormateada = formatearFecha(datosDictamen.fecha);
            
            const comentario = `Dictamen recibido\nResultado: ${datosDictamen.resultado}\nFecha de notificación: ${fechaNotificacionFormateada}\nPorcentaje de incapacidad: ${porcentaje}%\nFecha límite para apelar: ${fechaLimiteFormateada}`;

            // Determinar el siguiente estado según el resultado
            if (datosDictamen.resultado === "Aprobado" || porcentaje >= 66) {
                // Si es Aprobado o porcentaje >= 66%, pasar a estado Completo (8)
                const cambioEstadoData = {
                    idEstado: 8, // Completo
                    comentario: `${comentario}\n\n✅ Trámite completado exitosamente. El dictamen fue favorable con un porcentaje de incapacidad del ${porcentaje}%.`
                };
                await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);
                toast.success(`Dictamen cargado. El trámite ha sido completado exitosamente (${porcentaje}% >= 66%).`);
            } else if (datosDictamen.resultado === "Rechazado" || porcentaje < 66) {
                // Si es Rechazado o porcentaje < 66%, pasar a Primera Apelación (9)
                const cambioEstadoData = {
                    idEstado: 9, // Primera Apelación
                    comentario: comentario
                };
                await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);
                toast.success(`Dictamen cargado. El trámite ha sido movido a "Primera Apelación" (porcentaje ${porcentaje}% < 66%).`);
            }

            setMostrarFormularioDictamen(false);
            setArchivos([]);
            setDatosDictamen({ resultado: '', fecha: obtenerFechaActual(), porcentaje: '' });
            if (onEstadoCambiado) await onEstadoCambiado();

        } catch (error) {
            console.error("Error al confirmar dictamen:", error);
            toast.error("Error al procesar el dictamen: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
        }
    };

    const handleToggleCanal = (canal) => {
        setCanalesSeleccionados(prev => ({
            ...prev,
            [canal]: !prev[canal]
        }));
    };

    const handleEnviarNotificacion = async () => {
        // Validar que al menos un canal esté seleccionado
        const algunCanalSeleccionado = Object.values(canalesSeleccionados).some(v => v);
        if (!algunCanalSeleccionado) {
            toast.warning("Debe seleccionar al menos un canal de notificación.");
            return;
        }

        setEnviandoNotificacion(true);
        try {
            console.info('[GestorEstadoEsperaDictamen] Iniciando envío de notificación', {
                idTramite: tramite?.idTramite,
                canalesSeleccionados
            });

            // Crear un evento temporal para la notificación sobre el estado del dictamen
            const eventoNotificacion = {
                titulo: `Actualización: Espera de Dictamen - ${tramite.cliente?.nombre || ''} ${tramite.cliente?.apellido || ''}`,
                descripcion: `Se informa que el trámite continúa en espera del dictamen médico de ANSES. Le notificaremos cuando haya novedades.`,
                fecha: new Date().toISOString().split('T')[0], // Fecha actual
                horaInicio: new Date().toTimeString().slice(0, 5), // Hora actual
                horaFin: new Date(Date.now() + 3600000).toTimeString().slice(0, 5), // +1 hora
                todoElDia: false,
                tipoEvento: 'Recordatorio',
                idTramite: tramite.idTramite,
                visibilidad: 'POR_AREA',
                canalesNotificacion: {
                    whatsapp: false,
                    email: false,
                    inapp: true
                }
            };

            const response = await EventoService.crearEvento(eventoNotificacion);
            const idEvento = response.data.idEvento || response.data;

            console.info('[GestorEstadoEsperaDictamen] Evento temporal creado', {
                idEvento,
                response: response.data
            });

            // Enviar notificación manual inmediata
            const notificacionResponse = await NotificacionService.enviarNotificacionManual(idEvento, {
                notificarCliente: true,
                notificarContacto: false
            });

            console.info('[GestorEstadoEsperaDictamen] Respuesta envío notificación', {
                status: notificacionResponse.status,
                data: notificacionResponse.data
            });

            if (notificacionResponse.data?.exito === false) {
                throw new Error(notificacionResponse.data?.error || 'El backend indicó que el envío falló');
            }

            const canalesTexto = eventoNotificacion.canalesNotificacion
                .map(c => c === 'email' ? 'Email' : 'Aplicación')
                .join(', ');

            toast.success(`Notificación enviada al cliente por: ${canalesTexto}`);
            setMostrarModalNotificacion(false);
            setCanalesSeleccionados({ whatsapp: false, email: false, inapp: false });

        } catch (error) {
            console.error("[GestorEstadoEsperaDictamen] Error al enviar notificación:", {
                message: error?.message,
                status: error?.response?.status,
                data: error?.response?.data
            });
            toast.error("Error al enviar la notificación: " + (
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error.message
            ));
        } finally {
            setEnviandoNotificacion(false);
        }
    };

    const renderFormularioDictamen = () => (
        <div className="card shadow-sm border-0 rounded-4 p-4 mt-4">
            <h5 className="fw-semibold text-primary mb-3">Cargar Dictamen</h5>
            <div className="row g-3">
                <div className="col-md-4">
                    <label className="form-label fw-semibold">Resultado del Dictamen <span className="text-danger">*</span></label>
                    <select className="form-select" value={datosDictamen.resultado} onChange={e => setDatosDictamen({ ...datosDictamen, resultado: e.target.value })} required>
                        <option value="">Seleccionar...</option>
                        <option value="Aprobado">Aprobado</option>
                        <option value="Rechazado">Rechazado</option>
                    </select>
                </div>
                <div className="col-md-4">
                    <label className="form-label fw-semibold">Fecha de Notificación <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={datosDictamen.fecha} onChange={e => setDatosDictamen({ ...datosDictamen, fecha: e.target.value })} required />
                </div>
                <div className="col-md-4">
                    <label className="form-label fw-semibold">Porcentaje de Incapacidad (%) <span className="text-danger">*</span></label>
                    <input 
                        type="number" 
                        className="form-control" 
                        min="0" 
                        max="100" 
                        step="0.01"
                        value={datosDictamen.porcentaje} 
                        onChange={e => {
                            const valor = e.target.value;
                            setDatosDictamen({ ...datosDictamen, porcentaje: valor });
                            // Si el porcentaje es < 66%, mostrar información sobre apelación
                            if (valor && parseFloat(valor) < 66) {
                                setMostrarOpcionApelacion(true);
                            } else {
                                setMostrarOpcionApelacion(false);
                            }
                        }} 
                        required 
                        placeholder="Ej: 45.5"
                    />
                    {mostrarOpcionApelacion && datosDictamen.porcentaje && (
                        <small className="text-warning d-block mt-1">
                            ⚠️ Porcentaje menor a 66%. Se habilitará la opción de apelar.
                        </small>
                    )}
                    {datosDictamen.resultado && datosDictamen.porcentaje && (
                        (datosDictamen.resultado === "Rechazado" && parseFloat(datosDictamen.porcentaje) >= 66) ||
                        (datosDictamen.resultado === "Aprobado" && parseFloat(datosDictamen.porcentaje) < 66)
                    ) && (
                        <small className="text-danger d-block mt-1 fw-semibold">
                            ❌ Inconsistencia: {datosDictamen.resultado === "Rechazado" 
                                ? "Un dictamen Rechazado debe tener menos de 66%" 
                                : "Un dictamen Aprobado debe tener 66% o más"}
                        </small>
                    )}
                </div>
            </div>

            {/* Gestor de Archivos */}
            <div className="mt-4">
                <h6 className="fw-semibold text-primary">Adjuntar Archivo del Dictamen</h6>
                {archivos.length > 0 && (
                    <div className="list-group mb-3">
                        {archivos.map(archivo => (
                            <div key={archivo.id} className="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                    <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                    <span className="fw-semibold">{archivo.nombre}</span>
                                    <small className="text-muted ms-2">({archivo.tamaño})</small>
                                    <span className={`badge ms-2 ${archivo.estado === 'subido' ? 'bg-success' : archivo.estado === 'error' ? 'bg-danger' : 'bg-warning text-dark'}`}>{archivo.estado}</span>
                                </div>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleEliminarArchivo(archivo.id)} title="Eliminar"><DeleteIcon fontSize="small" /></button>
                            </div>
                        ))}
                    </div>
                )}
                <div className={`border rounded-3 p-4 text-center cursor-pointer ${dragActive ? "border-primary bg-primary bg-opacity-10" : "border-dashed"}`}
                     onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                     onClick={() => fileInputRef.current.click()}>
                    <input type="file" ref={fileInputRef} onChange={handleSeleccionarArchivos} className="d-none" />
                    <CloudUploadIcon className="text-muted mb-2" />
                    <p className="mb-0">Arrastra el archivo aquí o haz clic para seleccionar</p>
                </div>
            </div>

            <div className="d-flex justify-content-center gap-3 mt-4">
                <button className="btn btn-primary rounded-pill px-4" onClick={handleConfirmarDictamen} disabled={cargando || subiendoArchivos}>
                    {cargando ? 'Guardando...' : 'Confirmar Carga'}
                </button>
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setMostrarFormularioDictamen(false)} disabled={cargando}>
                    Cancelar
                </button>
            </div>
        </div>
    );

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-info mb-0">Espera de 1° Dictamen</h4>
                <span className="badge bg-info rounded-pill px-3 py-2">
                    <HourglassEmptyIcon fontSize="small" className="me-1" />
                    Pendiente
                </span>
            </div>

            <div className="alert alert-info border-0 rounded-3">
                <strong>Siguiente Paso:</strong>
                <p className="mb-0 mt-1">Esperar la resolución de ANSES. Cuando llegue, cargar el dictamen para continuar.</p>
            </div>

            {cargandoJunta ? <div className="alert alert-light">Cargando...</div> : (
                <div className="mt-1 p-3 bg-light rounded-3 border">
                    <h6 className="fw-semibold text-secondary mb-3">Seguimiento del dictamen</h6>
                    <div className="row g-3">
                        <div className="col-md-6 d-flex align-items-center">
                            <EventIcon className="text-muted me-2" />
                            <div>
                                <small className="text-muted d-block">Fecha de Junta</small>
                                <strong className="text-dark">{formatearFecha(juntaInfo?.fecha)}</strong>
                            </div>
                        </div>
                        <div className="col-md-6 d-flex align-items-center">
                            <TimerIcon className="text-muted me-2" />
                            <div>
                                <small className="text-muted d-block">Plazo Estimado</small>
                                <strong className="text-dark">{calcularPlazo(juntaInfo?.fecha)}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {mostrarFormularioDictamen ? renderFormularioDictamen() : (
                <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                    <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold" onClick={() => setMostrarFormularioDictamen(true)}>
                        <UploadFileIcon fontSize="small" /> Cargar Dictamen
                    </button>
                </div>
            )}

            {/* Modal de Notificación al Cliente */}
            {mostrarModalNotificacion && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg rounded-4">
                            <div className="modal-header bg-info bg-opacity-10 border-0 rounded-top-4">
                                <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                                    <NotificationsActiveIcon className="text-info" />
                                    Notificar al Cliente
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => setMostrarModalNotificacion(false)}
                                    disabled={enviandoNotificacion}
                                ></button>
                            </div>
                            <div className="modal-body p-4">
                                <p className="mb-3">
                                    Seleccione los canales por los cuales desea notificar al cliente sobre el estado del dictamen:
                                </p>
                                
                                <div className="d-flex flex-column gap-3">

                                    {/* Email */}
                                    <div 
                                        className={`card border rounded-3 p-3 cursor-pointer ${canalesSeleccionados.email ? 'border-primary bg-primary bg-opacity-10' : ''}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleToggleCanal('email')}
                                    >
                                        <div className="form-check">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                checked={canalesSeleccionados.email}
                                                onChange={() => {}}
                                                style={{ cursor: 'pointer' }}
                                            />
                                            <label className="form-check-label d-flex align-items-center gap-2 w-100" style={{ cursor: 'pointer' }}>
                                                <EmailIcon className="text-primary" />
                                                <div>
                                                    <strong>Email</strong>
                                                    <small className="d-block text-muted">Correo electrónico formal</small>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-info border-0 rounded-3 mt-3 mb-0">
                                    <small>
                                        <strong>Mensaje:</strong> Se notificará al cliente que el trámite continúa en espera del dictamen médico.
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer border-0 d-flex justify-content-center gap-3 pb-4">
                                <button 
                                    type="button" 
                                    className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4"
                                    onClick={() => setMostrarModalNotificacion(false)}
                                    disabled={enviandoNotificacion}
                                >
                                    <CancelIcon fontSize="small" />
                                    Cancelar
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-info d-flex align-items-center gap-2 rounded-pill px-4"
                                    onClick={handleEnviarNotificacion}
                                    disabled={enviandoNotificacion}
                                >
                                    <CheckCircleIcon fontSize="small" />
                                    {enviandoNotificacion ? 'Enviando...' : 'Enviar Notificación'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GestorEstadoEsperaDictamen;
